/**
 * V17.5: useNotifications Hook
 *
 * Provides real-time notification polling and state management:
 * - Polls for new notifications every 30 seconds
 * - Manages notification list and unread count
 * - Provides actions to mark as read, dismiss, etc.
 * - Shows toast for new high-priority notifications
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Notification,
  NotificationCategory,
  getNotifications,
  markAsRead as markAsReadApi,
  markAllAsRead as markAllAsReadApi,
  dismissNotification as dismissNotificationApi,
} from '../services/notificationService';

// V17.8: Polling interval in milliseconds (10 seconds for more real-time feel)
const POLL_INTERVAL = 10000;

interface UseNotificationsOptions {
  /** Enable/disable polling (default: true) */
  enablePolling?: boolean;
  /** Custom polling interval in ms (default: 30000) */
  pollInterval?: number;
  /** Max notifications to fetch (default: 50) */
  limit?: number;
}

interface UseNotificationsResult {
  /** List of notifications */
  notifications: Notification[];
  /** Number of unread notifications */
  unreadCount: number;
  /** Whether currently loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch notifications manually */
  refetch: () => Promise<void>;
  /** Mark a single notification as read */
  markAsRead: (notificationId: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Dismiss a notification */
  dismiss: (notificationId: string) => Promise<void>;
  /** New notification for toast display (clears after shown) */
  newNotification: Notification | null;
  /** Clear the new notification toast */
  clearNewNotification: () => void;
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsResult {
  const {
    enablePolling = true,
    pollInterval = POLL_INTERVAL,
    limit = 50,
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newNotification, setNewNotification] = useState<Notification | null>(null);

  // Track previous unread count to detect new notifications
  const prevUnreadCountRef = useRef<number>(0);
  // Track seen notification IDs to detect truly new ones
  const seenIdsRef = useRef<Set<string>>(new Set());
  // V17.13.1: Track consecutive network errors to avoid log spam
  const networkErrorCountRef = useRef<number>(0);

  // V17.13.1: Helper to check if error is network-related (should be silent)
  const isNetworkError = (err: unknown): boolean => {
    if (err && typeof err === 'object') {
      const axiosError = err as { code?: string; message?: string };
      const networkCodes = ['ERR_NETWORK', 'ERR_NETWORK_CHANGED', 'ERR_INTERNET_DISCONNECTED', 'ECONNABORTED'];
      if (axiosError.code && networkCodes.includes(axiosError.code)) return true;
      if (axiosError.message?.includes('Network Error')) return true;
    }
    return false;
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await getNotifications(limit, false);

      // Reset network error count on success
      networkErrorCountRef.current = 0;

      // Check for new notifications (not seen before)
      const newItems = response.notifications.filter(
        (n) => !seenIdsRef.current.has(n.id) && !n.is_read
      );

      // Update seen IDs
      response.notifications.forEach((n) => seenIdsRef.current.add(n.id));

      // If there are new notifications and this isn't the first load
      if (newItems.length > 0 && seenIdsRef.current.size > response.notifications.length) {
        // Show toast for the most recent new notification
        // Prioritize SLA breaches and lead assignments
        const priority = [
          NotificationCategory.SLA_BREACH,
          NotificationCategory.LEAD_ASSIGNMENT,
          NotificationCategory.LEAD_UPDATE,
          NotificationCategory.LEAD_RATING,
          NotificationCategory.SYSTEM,
        ];

        const sortedNew = [...newItems].sort((a, b) => {
          const aIdx = priority.indexOf(a.category as NotificationCategory);
          const bIdx = priority.indexOf(b.category as NotificationCategory);
          return aIdx - bIdx;
        });

        setNewNotification(sortedNew[0]);
      }

      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
      prevUnreadCountRef.current = response.unread_count;
      setError(null);
    } catch (err) {
      // V17.13.1: Silently handle network errors (expected when offline/network changes)
      if (isNetworkError(err)) {
        networkErrorCountRef.current++;
        // Only log once every 10 consecutive network errors to reduce noise
        if (networkErrorCountRef.current === 1 || networkErrorCountRef.current % 10 === 0) {
          console.debug('[Notifications] Network unavailable, will retry...');
        }
        // Don't show error to user for network issues
        return;
      }

      // For other errors, log and show to user
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  // Initial fetch and polling setup
  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Set up polling if enabled
    if (enablePolling) {
      const intervalId = setInterval(fetchNotifications, pollInterval);
      return () => clearInterval(intervalId);
    }
  }, [fetchNotifications, enablePolling, pollInterval]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markAsReadApi(notificationId);

      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Refetch on error to sync state
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await markAllAsReadApi();

      // Optimistic update
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Dismiss notification
  const dismiss = useCallback(async (notificationId: string) => {
    try {
      await dismissNotificationApi(notificationId);

      // Optimistic update - remove from list
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

      // Update unread count if the dismissed notification was unread
      setNotifications((prev) => {
        const dismissed = prev.find((n) => n.id === notificationId);
        if (dismissed && !dismissed.is_read) {
          setUnreadCount((count) => Math.max(0, count - 1));
        }
        return prev.filter((n) => n.id !== notificationId);
      });
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Clear new notification (after toast is shown)
  const clearNewNotification = useCallback(() => {
    setNewNotification(null);
  }, []);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismiss,
    newNotification,
    clearNewNotification,
  };
}

export default useNotifications;
